import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search,
  ShoppingCart,
  Menu,
  Phone,
  ChevronDown,
  MapPin,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { ANNOUNCEMENTS, CONTACT, LOGO_URL } from "../mock";
import { fetchMegaMenu } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { Button } from "./ui/button";
import ProductsMegaMenu from "./ProductsMegaMenu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";

const HELP_LINKS = [
  { label: "Appliance Model Lookup", to: "/model-lookup" },
  { label: "Contact Us", to: "/contact" },
];

const MAPS_HREF =
  CONTACT.mapsUrl ||
  `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}`;

const LOCATIONS = [
  { label: "Voir localisation maps live", href: MAPS_HREF },
  { label: CONTACT.address, href: MAPS_HREF },
];

export default function Header() {
  const { count } = useStore();
  const [query, setQuery] = useState("");
  /** Exclusive nav menu: null | 'products' | 'help' | 'locations' */
  const [openMenu, setOpenMenu] = useState(null);
  const navRef = useRef(null);
  const navigate = useNavigate();

  const { data: megaMenu = [] } = useQuery({
    queryKey: ["mega-menu"],
    queryFn: fetchMegaMenu,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!openMenu) return undefined;
    const close = () => setOpenMenu(null);
    window.addEventListener("scroll", close, { passive: true });
    const onKey = (e) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [openMenu]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const onPointerDown = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openMenu]);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setOpenMenu(null);
    navigate(`/boutique?q=${encodeURIComponent(q)}`);
  };

  const productsOpen = openMenu === "products";
  const helpOpen = openMenu === "help";
  const locationsOpen = openMenu === "locations";

  return (
    <header className="sticky top-0 z-50">
      {/* Top promo bar */}
      <div className="bg-rp-dark text-white text-xs overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee py-2">
          {[...ANNOUNCEMENTS, ...ANNOUNCEMENTS].map((a, i) => (
            <span key={i} className="mx-6 inline-flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-rp-green" />
              {a}
            </span>
          ))}
        </div>
      </div>

      {/* Logo + search row */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 py-3 flex items-center gap-4">
          <Sheet>
            <SheetTrigger asChild>
              <button type="button" className="lg:hidden p-2 -ml-2 text-slate-700" aria-label="Menu">
                <Menu className="h-6 w-6" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="text-left">Products</SheetTitle>
              </SheetHeader>
              <nav className="mt-4 flex flex-col gap-4">
                {megaMenu.map((section) => (
                  <div key={section.slug}>
                    <p className="text-xs font-bold uppercase text-rp-green mb-2">{section.name}</p>
                    <div className="space-y-1">
                      {section.items.map((item) =>
                        item.is_link_only ? (
                          <a
                            key={item.slug}
                            href={item.href}
                            className="block px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 rounded"
                          >
                            {item.name}
                          </a>
                        ) : (
                          <Link
                            key={item.slug}
                            to={`/boutique?menu=${item.slug}`}
                            className="block px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 rounded"
                          >
                            {item.name}
                          </Link>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </nav>
            </SheetContent>
          </Sheet>

          <Link to="/" className="flex items-center shrink-0 gap-3" aria-label={CONTACT.brand}>
            <img
              src={LOGO_URL}
              alt={CONTACT.brand}
              className="h-14 md:h-16 w-auto object-contain"
            />
            <span className="hidden xl:flex flex-col leading-tight">
              <span className="text-sm font-extrabold text-slate-900 tracking-tight">
                {CONTACT.brand}
              </span>
              <span className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                {CONTACT.tagline}
              </span>
            </span>
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-3xl mx-auto">
            <div className="relative w-full flex">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for a part or model #"
                className="w-full h-11 pl-4 pr-4 rounded-l border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-rp-green/40 text-sm"
              />
              <Button
                type="submit"
                className="h-11 rounded-l-none rounded-r bg-rp-green hover:bg-rp-green-dark px-5"
              >
                <Search className="h-5 w-5" />
              </Button>
            </div>
          </form>

          <div className="ml-auto flex items-center gap-2">
            <a
              href={`tel:${CONTACT.phoneRaw}`}
              className="hidden xl:flex items-center gap-2 text-sm text-slate-700 hover:text-rp-green mr-2"
            >
              <Phone className="h-4 w-4 text-rp-green" />
              <span className="font-semibold">{CONTACT.phone}</span>
            </a>
            <Link to="/panier" className="relative">
              <Button variant="outline" className="rounded h-11 px-4 gap-2 border-slate-300">
                <ShoppingCart className="h-5 w-5" />
                <span className="hidden sm:inline">Cart</span>
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-rp-green text-white text-[11px] font-bold flex items-center justify-center">
                    {count}
                  </span>
                )}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Dark nav bar — exclusive dropdowns */}
      <div
        ref={navRef}
        className="relative bg-rp-nav border-b border-black/20 z-40"
        onMouseLeave={() => setOpenMenu(null)}
      >
        <div className="max-w-[1400px] mx-auto px-4 flex items-center h-11">
          <button
            type="button"
            className={`flex items-center gap-2 h-11 px-4 text-sm font-semibold text-white shrink-0 transition-colors ${
              productsOpen ? "bg-rp-green" : "hover:bg-white/10"
            }`}
            aria-expanded={productsOpen}
            aria-haspopup="true"
            onMouseEnter={() => setOpenMenu("products")}
            onFocus={() => setOpenMenu("products")}
            onClick={() => setOpenMenu((v) => (v === "products" ? null : "products"))}
          >
            Products
            <ChevronDown className={`h-4 w-4 transition-transform ${productsOpen ? "rotate-180" : ""}`} />
          </button>

          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("help")}
            onFocus={() => setOpenMenu("help")}
          >
            <button
              type="button"
              className={`list-none flex items-center gap-1 h-11 px-4 text-sm font-medium text-white cursor-pointer ${
                helpOpen ? "bg-white/15 ring-1 ring-inset ring-white/40" : "hover:bg-white/10"
              }`}
              aria-expanded={helpOpen}
              aria-haspopup="true"
              onClick={() => setOpenMenu((v) => (v === "help" ? null : "help"))}
            >
              Help <ChevronDown className="h-4 w-4" />
            </button>
            {helpOpen && (
              <div className="absolute left-0 top-full min-w-[220px] bg-white border border-slate-200 shadow-lg py-2 z-[60]">
                {HELP_LINKS.map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    onClick={() => setOpenMenu(null)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-rp-green"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setOpenMenu("locations")}
            onFocus={() => setOpenMenu("locations")}
          >
            <button
              type="button"
              className={`list-none flex items-center gap-1 h-11 px-4 text-sm font-medium text-white cursor-pointer ${
                locationsOpen ? "bg-white/15 ring-1 ring-inset ring-white/40" : "hover:bg-white/10"
              }`}
              aria-expanded={locationsOpen}
              aria-haspopup="true"
              onClick={() => setOpenMenu((v) => (v === "locations" ? null : "locations"))}
            >
              Locations <ChevronDown className="h-4 w-4" />
            </button>
            {locationsOpen && (
              <div className="absolute left-0 top-full min-w-[260px] bg-white border border-slate-200 shadow-lg py-2 z-[60]">
                {LOCATIONS.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpenMenu(null)}
                    className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-rp-green"
                  >
                    {l.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          <Link
            to="/boutique"
            className="hidden md:flex h-11 px-4 text-sm font-medium text-white hover:bg-white/10 items-center"
            onMouseEnter={() => setOpenMenu(null)}
          >
            Shop All
          </Link>

          <span className="hidden lg:flex items-center gap-1 ml-auto text-xs text-white/80">
            <MapPin className="h-3.5 w-3.5 text-rp-green" />
            {CONTACT.city}
          </span>
        </div>

        {productsOpen && (
          <div
            className="relative z-50"
            onMouseEnter={() => setOpenMenu("products")}
          >
            <ProductsMegaMenu
              sections={megaMenu}
              onNavigate={() => setOpenMenu(null)}
            />
          </div>
        )}
      </div>

      {/* Mobile search */}
      <form onSubmit={submitSearch} className="md:hidden px-4 py-2 bg-slate-50 border-b border-slate-200">
        <div className="flex">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for a part or model #"
            className="flex-1 h-10 px-3 rounded-l border border-slate-300 text-sm"
          />
          <button type="submit" className="h-10 px-4 bg-rp-green text-white rounded-r">
            <Search className="h-4 w-4" />
          </button>
        </div>
      </form>
    </header>
  );
}
